import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Navigates to a section, handling both same-page scrolling and cross-page navigation
   * @param sectionId - The ID of the target section
   */
  const navigateToSection = (sectionId: string): void => {
    // If we're already on the home page, just scroll to the section
    if (location.pathname === '/') {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Navigate to home page and then scroll to section
      navigate('/', { replace: true });
      // Wait a bit for navigation to complete, then scroll
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  return (
    <footer className="bg-secondary-900 text-white">
      <div className="container-padding py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="md:col-span-2">
            <Link to="/" className="flex items-center space-x-2 mb-4">
              <img
                src="/images/tl-1-64.ico"
                alt="TherapyLink Logo - Special Education Therapy Platform"
                className="w-8 h-8"
                width="32"
                height="32"
                loading="lazy"
              />
              <span className="text-2xl font-bold">TherapyLink</span>
            </Link>
            <p className="text-secondary-300 mb-6 max-w-md">
              AI-powered platform connecting students with qualified therapy
              providers. Secure, FERPA-compliant, and designed for special
              education therapy companies.
            </p>
            <div className="flex space-x-4">
              <a
                href="mailto:info@therapy-link.org"
                className="text-secondary-300 hover:text-white transition-colors duration-200"
              >
                info@therapy-link.org
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <button
                  onClick={() => navigateToSection('features')}
                  className="text-secondary-300 hover:text-white transition-colors duration-200"
                >
                  Features
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigateToSection('workflow')}
                  className="text-secondary-300 hover:text-white transition-colors duration-200"
                >
                  How It Works
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigateToSection('contact')}
                  className="text-secondary-300 hover:text-white transition-colors duration-200"
                >
                  Contact
                </button>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Legal</h3>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/privacy"
                  className="text-secondary-300 hover:text-white transition-colors duration-200"
                >
                  Privacy Policy
                </Link>
              </li>
              {/* <li>
                <Link
                  to="/terms"
                  className="text-secondary-300 hover:text-white transition-colors duration-200"
                >
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link
                  to="/security"
                  className="text-secondary-300 hover:text-white transition-colors duration-200"
                >
                  Security
                </Link>
              </li> */}
            </ul>
          </div>
        </div>

        <div className="border-t border-secondary-800 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center mb-4">
            <p className="text-secondary-400 text-sm">
              © {currentYear} TherapyLink. All rights reserved.
            </p>
            <p className="text-secondary-400 text-sm mt-2 md:mt-0">
              FERPA Compliant • HIPAA Ready • Enterprise Security
            </p>
          </div>
          <div className="text-center">
            <p className="text-secondary-500 text-sm">
              Powered by{' '}
              <a
                href="https://redducklabs.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-400 hover:text-primary-300 transition-colors duration-200 font-medium"
              >
                Red Duck Labs
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
